import React from 'react';
import './PawKit.css';

function PawKit() {
  return (
    <div className="pawkit">
      <section className="pawkit-header">
        <h1>PawKit</h1>
        <p>Connect pet owners with trusted pet sitters</p>
      </section>

      <section className="app-showcase">
        <div className="content-wrapper">
          <div className="app-detail">
            <div className="app-info">
              <div className="app-description">
                <p>
                  PawKit bridges the gap between pet owners and pet sitters by creating comprehensive, 
                  easy-to-access pet profiles. No more scrambling to remember feeding schedules, 
                  medication times, or vet phone numbers. Everything your pet sitter needs to know 
                  is organized in one convenient place.
                </p>
                
                <h3>Key Features</h3>
                <ul>
                  <li><strong>Complete Pet Profiles:</strong> Detailed information for each pet including breed, age, temperament, and special needs</li>
                  <li><strong>Feeding Schedules:</strong> Exact feeding times, portion sizes, and dietary restrictions</li>
                  <li><strong>Medication Management:</strong> Dosage instructions, timing, and administration notes</li>
                  <li><strong>Emergency Contacts:</strong> Vet information, emergency clinics, and owner contact details</li>
                  <li><strong>Daily Routines:</strong> Walk schedules, playtime preferences, and behavioral quirks</li>
                  <li><strong>Photo Gallery:</strong> Help sitters identify pets and understand their personalities</li>
                </ul>

                <h3>Why PawKit?</h3>
                <p>
                  We built PawKit because finding trustworthy pet care shouldn't be stressful, and neither 
                  should providing it. Pet owners want peace of mind knowing their furry family members 
                  are in good hands, while pet sitters want clear, organized information to provide the 
                  best possible care. PawKit makes both sides of pet sitting seamless and stress-free.
                </p>

                <h3>Perfect For</h3>
                <ul>
                  <li>🐕 <strong>Dog Owners:</strong> Share walking routes, favorite toys, and behavioral notes</li>
                  <li>🐱 <strong>Cat Parents:</strong> Document litter preferences, hiding spots, and feeding quirks</li>
                  <li>🏠 <strong>Pet Sitters:</strong> Access all the information you need in one organized app</li>
                  <li>✈️ <strong>Travelers:</strong> Leave detailed care instructions without lengthy handwritten notes</li>
                </ul>

                <h3>How It Works</h3>
                <div className="how-it-works">
                  <div className="step">
                    <div className="step-number">1</div>
                    <div className="step-content">
                      <h4>Create Pet Profiles</h4>
                      <p>Add detailed information about each of your pets including photos, personality traits, and special needs</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-number">2</div>
                    <div className="step-content">
                      <h4>Set Up Care Instructions</h4>
                      <p>Document feeding schedules, medication routines, exercise needs, and emergency contacts</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-number">3</div>
                    <div className="step-content">
                      <h4>Connect with Sitters</h4>
                      <p>Share your PawKit profile with trusted pet sitters who can access all care information instantly</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-number">4</div>
                    <div className="step-content">
                      <h4>Enjoy Peace of Mind</h4>
                      <p>Relax knowing your pets are getting consistent, informed care while you're away</p>
                    </div>
                  </div>
                </div>

                <div className="pet-care-sections">
                  <h3>What You Can Document</h3>
                  <div className="care-categories">
                    <div className="care-category">
                      <h4>🍽️ Feeding & Nutrition</h4>
                      <ul>
                        <li>Meal times and portion sizes</li>
                        <li>Food brand and type preferences</li>
                        <li>Treats and restrictions</li>
                        <li>Water bowl locations</li>
                      </ul>
                    </div>
                    <div className="care-category">
                      <h4>💊 Health & Medications</h4>
                      <ul>
                        <li>Prescription medications and dosages</li>
                        <li>Veterinarian contact information</li>
                        <li>Emergency clinic details</li>
                        <li>Allergies and health conditions</li>
                      </ul>
                    </div>
                    <div className="care-category">
                      <h4>🎾 Exercise & Activities</h4>
                      <ul>
                        <li>Walking schedules and routes</li>
                        <li>Favorite toys and games</li>
                        <li>Energy levels and exercise needs</li>
                        <li>Park preferences and restrictions</li>
                      </ul>
                    </div>
                    <div className="care-category">
                      <h4>🏠 Home & Routine</h4>
                      <ul>
                        <li>Sleeping arrangements</li>
                        <li>Litter box locations and preferences</li>
                        <li>Door and gate protocols</li>
                        <li>Behavioral quirks and comfort items</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="app-store-section">
                  <p className="availability">Currently in Development</p>
                  <div className="coming-soon-badge">
                    <span className="badge-text">Coming Soon</span>
                  </div>
                  <p className="notify-text">Building the future of pet care communication!</p>
                </div>
              </div>
            </div>

            <div className="app-screenshots">
              <h3>Screenshots</h3>
              <div className="screenshots-grid">
                <div className="screenshot-item">
                  <div className="screenshot-placeholder">
                    <span>🐕</span>
                    <p>Pet Profiles</p>
                  </div>
                </div>
                <div className="screenshot-item">
                  <div className="screenshot-placeholder">
                    <span>🍽️</span>
                    <p>Feeding Schedule</p>
                  </div>
                </div>
                <div className="screenshot-item">
                  <div className="screenshot-placeholder">
                    <span>💊</span>
                    <p>Medication Tracker</p>
                  </div>
                </div>
                <div className="screenshot-item">
                  <div className="screenshot-placeholder">
                    <span>🏥</span>
                    <p>Emergency Contacts</p>
                  </div>
                </div>
                <div className="screenshot-item">
                  <div className="screenshot-placeholder">
                    <span>🚶‍♀️</span>
                    <p>Daily Routines</p>
                  </div>
                </div>
                <div className="screenshot-item">
                  <div className="screenshot-placeholder">
                    <span>📱</span>
                    <p>Sitter Dashboard</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PawKit;